import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { withTimeout, clearCache } from '@/lib/pageCache';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async (uid, attempt = 1) => {
    if (!uid) {
      setProfile(null);
      return null;
    }
    try {
      // 8s timeout, profile load mustn't hang the bootstrap. On timeout we
      // keep whatever profile is already in state (often hydrated from a
      // prior session) so the rest of the app stays usable.
      const { data, error } = await withTimeout(
        supabase.from('profiles').select('*').eq('id', uid).maybeSingle(),
        8000, 'auth.loadProfile',
      );
      if (error) console.warn('profile load:', error.message);
      if (data) {
        setProfile(data);
        return data;
      }
      // No row and no throw — retry once. A null profile blanks every gated
      // page, so it's worth one more attempt before giving up.
      if (attempt === 1) {
        await new Promise((r) => setTimeout(r, 1200));
        return loadProfile(uid, 2);
      }
      return null;
    } catch (e) {
      console.warn('profile load failed (keeping prior):', e?.message || e);
      if (attempt === 1) {
        await new Promise((r) => setTimeout(r, 1200));
        return loadProfile(uid, 2);
      }
      return null;
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    let resolved = false;
    // Safety net, if `onAuthStateChange` never fires for any reason
    // (browser quirk, supabase client deadlock), release the auth gate after
    // 8s so the rest of the app can render. The listener will still update
    // session/user state if/when it eventually arrives.
    const safety = setTimeout(() => {
      if (mounted && !resolved) {
        console.warn('Auth bootstrap safety net fired at 8s, releasing loading gate.');
        setLoading(false);
      }
    }, 8000);

    // Subscribe FIRST. In supabase-js v2 the listener fires `INITIAL_SESSION`
    // immediately on subscribe, supplying the session previously persisted in
    // localStorage WITHOUT making any network call. This is the supported v2
    // bootstrap pattern and is critical for tab-refocus / Safari resilience:
    //   * It does NOT block on a token refresh (the failure mode that pinned
    //     the page on "Loading…" for 8s in Phase 30 testing).
    //   * The cached session is delivered synchronously enough that
    //     ProtectedRoute renders children on the very next paint.
    //   * If the token is expired, `TOKEN_REFRESHED` follows shortly with the
    //     fresh session, we treat that as a quiet update (no re-fetches).
    const { data: sub } = supabase.auth.onAuthStateChange(async (event, sess) => {
      // First emission ends the bootstrap regardless of `event` type.
      if (mounted && !resolved) {
        resolved = true;
        clearTimeout(safety);
        setLoading(false);
      }
      setSession(sess);
      const nextUser = sess?.user || null;
      setUser((prev) => {
        // Preserve object identity when only the access_token / expiry has
        // changed. Downstream pages depend on `user?.id`, so this keeps
        // their useEffect deps stable across TOKEN_REFRESHED events and is
        // half of the refocus-resilience story (the other half is
        // page-level sessionStorage hydration in lib/pageCache.js).
        if (prev?.id && nextUser?.id && prev.id === nextUser.id) return prev;
        return nextUser;
      });
      // Token refreshes don't change profile data, so skip the lookup. This
      // saves a Supabase round-trip every ~1 hour and on every tab refocus.
      if (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') return;
      if (nextUser?.id) {
        // Fire and forget, profile load must not block the auth gate.
        loadProfile(nextUser.id).catch(() => {});
      } else {
        setProfile(null);
      }
    });

    return () => {
      mounted = false;
      clearTimeout(safety);
      sub?.subscription?.unsubscribe?.();
    };
  }, [loadProfile]);

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signUp = async (email, password, firstName, lastName) => {
    const fn = (firstName || '').trim();
    const ln = (lastName || '').trim();
    const composedName = [fn, ln].filter(Boolean).join(' ');
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        // Stored on `auth.users.raw_user_meta_data`. The Supabase trigger that
        // creates the `profiles` row reads first_name/last_name (and falls
        // back to `name` for backward compat with older sign-ups).
        data: { first_name: fn, last_name: ln, name: composedName },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signInWithGoogle = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: { access_type: 'offline', prompt: 'consent' },
      },
    });
    if (error) throw error;
    return data;
  };

  const resetPassword = async (email) => {
    const redirect = `${window.location.origin}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: redirect });
    if (error) throw error;
  };

  const updatePassword = async (newPassword) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  };

  const signOut = async () => {
    // Sign-out must NEVER hang the UI. If Supabase's network call to revoke the
    // token stalls (expired refresh token, slow network, etc), we still wipe
    // local session storage + in-memory state so the user is signed out
    // client-side regardless. The server-side revoke happens best-effort.
    const networkRevoke = supabase.auth.signOut().catch((e) => {
      console.warn('supabase.auth.signOut() failed (continuing anyway):', e?.message || e);
    });
    const timeout = new Promise((resolve) => setTimeout(resolve, 2500));
    await Promise.race([networkRevoke, timeout]);
    // Hard-clear any lingering Supabase session in localStorage so a stale
    // refresh token can't auto-restore the session on next load.
    try {
      Object.keys(localStorage).forEach((k) => {
        if (k.startsWith('sb-') && k.endsWith('-auth-token')) localStorage.removeItem(k);
      });
    } catch (e) { /* storage may be unavailable */ }
    // Phase 30, Also wipe the per-user page-cache entries so the next person
    // signing in on the same device doesn't see the previous user's data
    // briefly flash before their own data loads.
    try {
      Object.keys(sessionStorage).forEach((k) => {
        if (k.startsWith('2120_pd_')) sessionStorage.removeItem(k);
      });
    } catch (e) { /* storage may be unavailable */ }
    // Silence the unused-import lint for `clearCache`, re-exporting it as a
    // safety net for any caller that wants to invalidate specific entries.
    void clearCache;
    setSession(null);
    setUser(null);
    setProfile(null);
  };

  const refreshProfile = useCallback(async () => {
    if (user?.id) await loadProfile(user.id);
  }, [user, loadProfile]);

  const value = {
    session, user, profile, loading,
    signIn, signUp, signOut, signInWithGoogle, resetPassword, updatePassword,
    refreshProfile,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
